import { useState, useCallback } from 'react';
import { Button } from '../ui/button';

const SIZE = 3;
const IMAGES = [
  'https://images.unsplash.com/photo-1697033300784-6c9d143a30e2?crop=entropy&cs=srgb&fm=jpg&w=600&q=80',
  'https://images.unsplash.com/photo-1664329182818-f375993c0a00?crop=entropy&cs=srgb&fm=jpg&w=600&q=80',
  'https://images.unsplash.com/photo-1669829691116-1d1b367aaa59?crop=entropy&cs=srgb&fm=jpg&w=600&q=80'
];

const solved = Array.from({ length: SIZE * SIZE }, (_, i) => i);

const neighbours = (index) => {
  const x = index % SIZE;
  const y = Math.floor(index / SIZE);
  return [
    y > 0 ? index - SIZE : null,
    y < SIZE - 1 ? index + SIZE : null,
    x > 0 ? index - 1 : null,
    x < SIZE - 1 ? index + 1 : null
  ].filter((v) => v !== null);
};

const shuffleBoard = () => {
  let tiles = [...solved];
  let blank = SIZE * SIZE - 1;
  for (let i = 0; i < 200; i++) {
    const options = neighbours(blank);
    const pick = options[Math.floor(Math.random() * options.length)];
    [tiles[blank], tiles[pick]] = [tiles[pick], tiles[blank]];
    blank = pick;
  }
  if (tiles.every((v, i) => v === i)) return shuffleBoard();
  return tiles;
};

export function ImagePuzzle({ onScore }) {
  const [imageIndex, setImageIndex] = useState(0);
  const [tiles, setTiles] = useState(shuffleBoard);
  const [moves, setMoves] = useState(0);
  const [won, setWon] = useState(false);

  const reset = useCallback((nextImage = imageIndex) => {
    setImageIndex(nextImage);
    setTiles(shuffleBoard());
    setMoves(0);
    setWon(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [imageIndex]);

  const slide = (position) => {
    if (won) return;
    const blank = tiles.indexOf(SIZE * SIZE - 1);
    if (!neighbours(blank).includes(position)) return;
    const next = [...tiles];
    [next[blank], next[position]] = [next[position], next[blank]];
    setTiles(next);
    const total = moves + 1;
    setMoves(total);
    if (next.every((v, i) => v === i)) {
      setWon(true);
      onScore(total);
    }
  };

  return (
    <div className="space-y-4" data-testid="game-image-puzzle">
      <div className="flex items-center justify-between text-sm">
        <span>Moves: <span className="text-amber-300 tabular-nums" data-testid="puzzle-moves">{moves}</span></span>
        <span className="text-xs text-muted-foreground">Fewer moves = better score</span>
      </div>

      <div className="grid grid-cols-3 gap-[3px] p-1 rounded-lg border border-white/10 bg-black/50" data-testid="puzzle-board">
        {tiles.map((tile, position) => {
          const isBlank = tile === SIZE * SIZE - 1 && !won;
          const tx = (tile % SIZE) * (100 / (SIZE - 1));
          const ty = Math.floor(tile / SIZE) * (100 / (SIZE - 1));
          return (
            <button
              key={position}
              type="button"
              onClick={() => slide(position)}
              className={`aspect-square rounded-sm overflow-hidden transition-opacity ${isBlank ? 'opacity-0' : 'opacity-100 hover:brightness-110'}`}
              style={{
                backgroundImage: `url(${IMAGES[imageIndex]})`,
                backgroundSize: `${SIZE * 100}%`,
                backgroundPosition: `${tx}% ${ty}%`
              }}
              data-testid={`puzzle-tile-${position}`}
            />
          );
        })}
      </div>

      {won && <p className="text-center text-sm text-emerald-400" data-testid="puzzle-solved">Solved in {moves} moves!</p>}

      <div className="flex gap-2">
        <Button variant="outline" className="rounded-full flex-1" onClick={() => reset()} data-testid="puzzle-shuffle">Shuffle</Button>
        <Button
          variant="outline"
          className="rounded-full flex-1"
          onClick={() => reset((imageIndex + 1) % IMAGES.length)}
          data-testid="puzzle-change-image"
        >
          Change picture
        </Button>
      </div>
    </div>
  );
}
